/**
 * useGroupedData — Transforms flat data into grouped structure for DynamicTableV2.
 *
 * When schema.features.groupBy is configured, this hook:
 * 1. Groups data by the specified field(s)
 * 2. Calculates aggregates (sum, count, average, min, max) based on column definitions
 * 3. Returns a flat list of group header rows + child rows (when expanded)
 *
 * Date normalization strategy
 * ───────────────────────────
 * Date values from the API can arrive in many different string shapes, e.g.:
 *   "2025-08-06T00:00:00"          (ISO, no timezone)
 *   "2025-08-06T00:00:00Z"         (ISO UTC)
 *   "2025-08-06T04:00:00+00:00"    (ISO with offset — same day UTC, different local day)
 *   "08/06/2025"                   (MM/DD/YYYY, pre-formatted)
 *   "2025-08-06"                   (date-only ISO)
 *
 * The groupKey was previously derived via `String(rawValue)`, so rows with the
 * same calendar date but different time/timezone components produced separate
 * groups (e.g. "2025-08-06T00:00:00" vs "2025-08-06T04:00:00+00:00").
 *
 * Fix: for columns whose ColumnType is one of the date family ('date', 'dateTime',
 * 'dateTimeLTS', 'dateRange', 'conditionalDate') we normalise the raw value to a
 * canonical "YYYY-MM-DD" group key **before** bucketing, so every representation
 * of the same calendar date ends up in the same bucket.
 */

import { useMemo } from "react";
import type { InternalColumnDef } from "../schema/types";
import {
  calculateAggregate,
  formatGroupValue,
  isDateColumnType,
  normaliseDateToGroupKey,
} from "./grouping-utils";

export { isGroupRow } from "./grouping-utils";

export interface GroupedRow {
  /** Marks this as a group header row */
  __isGroupRow: true;
  /** Unique key for the group (stringified group value) */
  __groupKey: string;
  /** The value being grouped by */
  __groupValue: unknown;
  /** Display label for the group */
  __groupLabel: string;
  /** Number of rows in this group */
  __groupCount: number;
  /** Child rows belonging to this group */
  __subRows: Record<string, unknown>[];
  /** Calculated aggregate values for each column */
  __aggregates: Record<string, unknown>;
  /** Whether this group is expanded */
  __isExpanded: boolean;
}

export interface UseGroupedDataConfig {
  /** Raw data to group */
  data: unknown[];
  /** Field(s) to group by */
  groupByFields: ReadonlyArray<string>;
  /** Set of expanded group keys */
  expandedGroups: Set<string>;
  /** Column definitions (for aggregate calculations) */
  columns: ReadonlyArray<InternalColumnDef>;
  /** Whether groups should be expanded by default */
  defaultExpanded: boolean;
  /** Whether to hide the grouped column on child rows */
  hideGroupedColumn: boolean;
}

export interface UseGroupedDataResult {
  /** Flat list of rows: group headers + visible child rows */
  groupedData: (GroupedRow | Record<string, unknown>)[];
  /** All group keys */
  groupKeys: string[];
  /** Total count of all rows (for pagination) */
  totalRowCount: number;
}

/**
 * Hook to transform flat data into grouped structure
 */
export function useGroupedData(
  config: UseGroupedDataConfig,
): UseGroupedDataResult {
  const {
    data,
    groupByFields,
    expandedGroups,
    columns,
    defaultExpanded: _defaultExpanded,
    hideGroupedColumn,
  } = config;

  return useMemo(() => {
    // If no groupBy fields, return data as-is
    if (!groupByFields || groupByFields.length === 0) {
      return {
        groupedData: data as Record<string, unknown>[],
        groupKeys: [],
        totalRowCount: data.length,
      };
    }

    // Use the first field for grouping (single-level grouping like V1)
    const groupField = groupByFields[0];

    // Resolve the column definition for the group field so we can know its type
    // BEFORE we start iterating rows (used for date normalisation and display).
    const groupedColumn = columns.find((col) => col.field === groupField);
    const groupColumnType = groupedColumn?.type;
    const isDateGroup = isDateColumnType(groupColumnType);

    // Group data by the field value
    const groups = new Map<string, Record<string, unknown>[]>();
    const groupOrder: string[] = [];

    for (const row of data) {
      const rec = row as Record<string, unknown>;
      const groupValue = rec[groupField];

      // For date columns: normalise to "YYYY-MM-DD" so all representations of
      // the same calendar date are bucketed together regardless of time/timezone.
      // For all other types: keep the original String() conversion.
      const groupKey = groupValue == null
        ? "__null__"
        : isDateGroup
          ? normaliseDateToGroupKey(groupValue)
          : String(groupValue);

      if (!groups.has(groupKey)) {
        groups.set(groupKey, []);
        groupOrder.push(groupKey);
      }
      groups.get(groupKey)!.push(rec);
    }

    // Build aggregates map for columns that have aggregate defined
    const aggregateColumns = columns.filter((col) => col.aggregate != null);

    // Build the flat output list
    const groupedData: (GroupedRow | Record<string, unknown>)[] = [];
    const groupKeys: string[] = [];

    // (groupedColumn / groupColumnType already resolved above, before the rows loop)

    for (const groupKey of groupOrder) {
      const rows = groups.get(groupKey)!;
      // For date columns the canonical groupKey IS the display-ready value that
      // formatGroupValue will reformat to MM/DD/YYYY. For other types we keep the
      // first row's raw value as the display input (historical behaviour).
      const groupValue = isDateGroup ? groupKey : rows[0][groupField];
      // Expansion is controlled solely via expandedGroups. Any schema "defaultExpanded"
      // behavior should be implemented by initializing expandedGroups once upstream.
      const isExpanded = expandedGroups.has(groupKey);

      // Calculate aggregates for this group
      const aggregates: Record<string, unknown> = {};
      for (const col of aggregateColumns) {
        if (col.aggregate) {
          aggregates[col.field] = calculateAggregate(
            rows,
            col.field,
            col.aggregate,
          );
        }
      }

      // Create group header row
      const groupRow: GroupedRow = {
        __isGroupRow: true,
        __groupKey: groupKey,
        __groupValue: groupValue,
        __groupLabel: formatGroupValue(groupValue, groupColumnType),
        __groupCount: rows.length,
        __subRows: rows,
        __aggregates: aggregates,
        __isExpanded: isExpanded,
      };

      groupedData.push(groupRow);
      groupKeys.push(groupKey);

      // Add child rows if expanded
      if (isExpanded) {
        for (const row of rows) {
          groupedData.push({
            ...row,
            __parentGroupKey: groupKey,
            __groupedField: groupField,
          });
        }
      }
    }

    return {
      groupedData,
      groupKeys,
      totalRowCount: data.length,
    };
  }, [
    data,
    groupByFields,
    expandedGroups,
    columns,
    hideGroupedColumn,
  ]);
}

export default useGroupedData;
